import * as Marker from './lib/marker';
import {smileRegex, smileMap} from './smilies';

const MatchResult = Marker.MatchResult;

function buildMsgParser(featuresFunc) {
	let featureParser;

	let worldParser = Marker.simpleMatcher(ctx => {
		let message = [];
		let startOffset = ctx.offset;

		while (true) {
			let curOffset = ctx.offset;
			if (curOffset >= ctx.input.length) {
				if (curOffset > startOffset) {
					message.push({
						type: 'text',
						text: ctx.input.substr(startOffset, curOffset - startOffset),
					});
				}

				return MatchResult.ok(message);
			}

			let feature = featureParser.match(ctx);
			if (feature.isFail()) {
				ctx.shift(1);
				continue;
			}

			if (curOffset > startOffset) {
				message.push({
					type: 'text',
					text: ctx.input.substr(startOffset, curOffset - startOffset),
				});
			}

			message.push(feature.value);

			startOffset = ctx.offset;
		}
	});

	featureParser = Marker.first(featuresFunc(worldParser));

	return (msg, room) => worldParser.parse(msg, {room});
}

function makeQuoteParser(worldParser) {
	let quoteStart = Marker.str('> ');
	let quoteBody = Marker.regex(/^[^\n]*/m);
	let quoteEnd = Marker.regex(/^\n\n|$/);
	let quoteContinuation = Marker.regex(/^\n(?:> )?/m);
	let failEmpty = MatchResult.fail('Empty quotes not permitted');
	let fail = MatchResult.fail();

	return Marker.rollbackableMatcher(ctx => {
		if (!isBeginOfLine(ctx))
			return fail;
		if (quoteStart.match(ctx).isFail())
			return fail;

		let body = '';
		while (true) {
			let bodyResult = quoteBody.match(ctx);
			if (bodyResult.isFail())
				return fail;

			body += bodyResult.value[0];

			if (quoteEnd.match(ctx).isOk()) {
				break;
			}

			body += '\n';

			quoteContinuation.match(ctx);
		}

		if (body === '')
			return failEmpty;

		return MatchResult.ok({
			type: 'quote',
			processedBody: worldParser.parse(body, ctx.data),
		});
	});
}

function isBeginOfLine(ctx) {
	return ctx.offset === 0 || ctx.input[ctx.offset - 1] === '\n';
}

function makeMentionParser() {
	let fail = MatchResult.fail('No members in room');

	return Marker.rollbackableMatcher(ctx => {
		let matcher = ctx.data.mentionMatcher;
		if (!matcher) {
			let members = ctx.data.room.getMembers();
			if (members.length < 1) {
				matcher = Marker.simpleMatcher(ctx => fail);
			} else {
				let logins = members.map(x => RegExp.escape(x.name)).sort().reverse().join('|');

				matcher = Marker.regex(new RegExp('^@(' + logins + ')')).map(body => ({
					type: 'mention',
					user: members.find(x => x.name == body[1]),
				}));
			}

			ctx.data.mentionMatcher = matcher;
		}

		return matcher.match(ctx);
	});
}

function makeUrlParser() {
	let fail = MatchResult.fail();

	return Marker.all([
		Marker.simpleMatcher(ctx => {
			if (ctx.offset === 0 || ctx.input[ctx.offset] === 'h') {
				return MatchResult.ok();
			}
			return fail;
		}),

		Marker.lookupableRegex(/\bhttps?:\/\/[^\s"']+/).map(body => ({
			type: 'url',
			url: body[0],
		})),
	]).map(res => res[1]);
}

const newlineParser = Marker.str('\n').map(body => ({
	type: 'raw',
	html: '<br>',
}));

function makeRoomParser() {
	let fail = MatchResult.fail();
	let rxSpace = /\s/;

	return Marker.all([
		Marker.simpleMatcher(ctx => {
			if (ctx.offset === 0 || rxSpace.test(ctx.input[ctx.offset - 1])) {
				return MatchResult.ok();
			}
			return fail;
		}),

		Marker.regex(/^#[^\s<>]+/).map(body => ({
			type: 'room',
			room: body[0],
		})),
	]).map(res => res[1]);
}

export const preprocessMessage = buildMsgParser(worldParser => [
	makeQuoteParser(worldParser),

	Marker.regex(/^```\n?([\s\S]*?)\n?```/).map(body => ({
		type: 'codeblock',
		text: body[1],
	})),

	Marker.regex(/^`([^\n]*?)`/).map(body => ({
		type: 'code',
		text: body[1],
	})),

	makeUrlParser(),
	makeRoomParser(),
	makeMentionParser(),

	Marker.regex(smileRegex).map(body => ({
		type: 'smile',
		code: body[0],
		icon: smileMap[body[0]],
	})),

	newlineParser,
]);

export const preprocessSysMessage = buildMsgParser(worldParser => [
	makeUrlParser(),

	Marker.lookupableRegex(/^\/.+$/m).map(body => ({
		type: 'command',
		text: body[0],
	})),

	newlineParser,
]);
